// packages/core/src/learning/school-modules/debt-free-ramsey.ts
// ─────────────────────────────────────────────────────────────────────────
// Lens Pack for the 'debt-free-ramsey' school. LearningModule, surface 'school'.
//   module id   = 'school-debt-free-ramsey'  (matches exhibit nativeModuleId)
//   homeSchool  = 'debt-free-ramsey'
// PROMOTED: five authored lessons (above the divider in the index).
//
// Placeholder presentational fields (emoji, accentColor, tier, order) carried
// from the stub conventions — tune per-school in the presentation pass.
// ─────────────────────────────────────────────────────────────────────────

import type { LearningModule } from '../LearningTypes';

export const SCHOOL_DEBT_FREE_RAMSEY_MODULE: LearningModule = {
	id: 'school-debt-free-ramsey',
	title: 'Debt-Free (Ramsey)',
	description:
		'The lens that treats debt as a behavior emergency — payment pressure, snowball momentum, stolen options, and where the rules get too rigid.',
	emoji: '❄️',
	accentColor: '#5e4b8b',
	tier: 'control',
	pillar: 'debt-credit-interest',
	order: 0,
	surface: 'school',
	homeSchool: 'debt-free-ramsey',
	lessons: [
		{
			id: 'school-debt-free-ramsey-01',
			title: 'Debt as a behavior emergency',
			description:
				'This lesson teaches why Debt free Ramsey sees debt as a pressure problem that changes behavior, not just a number on a screen.',
			pillar: 'debt-credit-interest',
			schoolsOfThought: ['debt-free-ramsey'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['debt-free-ramsey', 'debt-pressure', 'payment-fatigue'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['debt pressure', 'behavior urgency', 'payment fatigue'],
			blocks: [
				{
					type: 'paragraph',
					text: 'You can understand interest rates and still feel trapped by payments. Debt free Ramsey notices that debt often changes your mood before it changes your spreadsheet.',
				},
				{
					type: 'paragraph',
					text: 'This lens treats debt as a behavior emergency because payments reduce freedom, increase stress, and make every paycheck feel claimed before it arrives.',
				},
				{
					type: 'bullet_list',
					items: [
						'debt steals attention',
						'payments shrink options',
						'urgency creates movement',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is treating debt as normal because everyone around you also has payments.',
				},
				{
					type: 'example',
					text: 'Imagine someone earning decent money but sending hundreds of dollars every month to old purchases, a car, and card balances. A pure math lens may ask which interest rate is highest. The Ramsey lens first asks why this person still feels broke after payday.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that this lens can feel blunt. It may ignore situations where careful credit use, lower interest debt, or strategic timing matters. But its strength is clarity when someone is overwhelmed.',
				},
				{
					type: 'paragraph',
					text: 'In Nisk, connect this lens to your Dashboard by looking at how many dollars are already spoken for before the month begins.',
				},
				{
					type: 'key_takeaway',
					text: 'Debt free Ramsey sees debt as pressure on your behavior, not just a balance.',
				},
			],
			audioScript:
				'Debt free Ramsey starts from a simple observation: debt is not only something you calculate. It is something you carry. You can know the interest rate. You can know the minimum payment. You can even know the payoff date. But if every paycheck arrives already claimed by past decisions, the real cost is not just financial. It is emotional. It is mental. It is the feeling that your money has already left before you even decide what you want your life to look like.\n\nThis is why the Ramsey lens treats debt like a behavior emergency. That does not mean every debt is the same. It does not mean every person with debt made bad choices. People deal with medical bills, family pressure, layoffs, school costs, bad timing, and normal mistakes. The point of this worldview is not shame. The point is that debt creates pressure, and pressure changes behavior.\n\nWhen you have payments everywhere, you often become reactive. You wait for payday, pay what must be paid, and then try to survive on what is left. You may avoid looking at the full picture because it feels heavy. You may tell yourself, at least I can afford the payment. Debt free Ramsey pushes back on that phrase. Affording the payment is not the same as being free. A payment can fit inside the month and still make your life smaller.\n\nThat is the key difference between this lens and a pure optimization lens. A math first person might say, sort everything by interest rate and attack the highest rate first. That can make sense on paper. The Ramsey lens asks a different question: what gets you moving when you are tired, embarrassed, scattered, or overwhelmed? Sometimes the first win matters more than the perfect order. Sometimes closing one account gives you energy. Sometimes seeing one payment disappear makes the whole plan feel possible.\n\nThe blind spot is real. This lens can become too rigid. It can underweight credit strategy, liquidity, and cases where the mathematically optimal move matters. It can also make people feel judged if the tone gets too harsh. Nisk should not use this worldview to shame people. It should use it to explain why simplicity and urgency can help.\n\nSo when you apply this lesson, do not ask only, what is my interest rate? Also ask, what is this debt doing to my behavior? Is it making me avoid my numbers? Is it making me delay goals? Is it stealing the feeling of control? That is what Debt free Ramsey notices. Debt is not just a math problem. It is a pressure system. Once you see that, the goal becomes clearer: reduce the pressure, build momentum, and create room for better choices.',
			flashcards: [
				{
					id: 'school-debt-free-ramsey-01-fc-1',
					role: 'main_idea',
					front: 'Why behavior emergency?',
					back: 'Debt can change stress, choices, and momentum before the math feels obvious.',
				},
				{
					id: 'school-debt-free-ramsey-01-fc-2',
					role: 'watch_out',
					front: 'What is the trap?',
					back: 'Thinking a payment is harmless just because it fits this month.',
				},
				{
					id: 'school-debt-free-ramsey-01-fc-3',
					role: 'use_it',
					front: 'How do you apply it?',
					back: 'Check which payments already control your paycheck before you choose anything else.',
				},
			],
			applyAction: {
				type: 'openDashboard',
				label: 'Review your payment pressure',
			},
			revision: 1,
		},
		{
			id: 'school-debt-free-ramsey-02',
			title: 'Why the snowball works emotionally',
			description:
				'This lesson teaches why Debt free Ramsey values quick wins and momentum when debt feels overwhelming.',
			pillar: 'debt-credit-interest',
			schoolsOfThought: ['debt-free-ramsey'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['debt-free-ramsey', 'debt-snowball', 'motivation-design'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'snowball momentum',
				'small balance wins',
				'motivation design',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'The debt snowball can look strange if you only judge it by interest rate. Debt free Ramsey is looking at something different: whether the plan keeps a real person moving.',
				},
				{
					type: 'paragraph',
					text: 'This lens believes motivation is part of the system. A plan that is mathematically neat but emotionally dead may not survive the first hard month.',
				},
				{
					type: 'bullet_list',
					items: [
						'close one balance',
						'feel progress fast',
						'turn effort into belief',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is assuming the best plan on paper is always the plan a tired person can follow.',
				},
				{
					type: 'example',
					text: 'Someone has three balances: $300, $1,200, and $4,500. The $4,500 balance has the highest rate, but the $300 balance can disappear quickly. The Ramsey lens says that first closed account can create proof: this is working.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that the snowball may cost more interest than another payoff order. This lens accepts that cost when the emotional win creates follow through.',
				},
				{
					type: 'paragraph',
					text: 'In Nisk, use Simulate to compare how a focused payoff plan changes your future options, then ask which plan you would actually stick with.',
				},
				{
					type: 'key_takeaway',
					text: 'The snowball is not only math. It is motivation design.',
				},
			],
			audioScript:
				'The debt snowball is one of the clearest examples of how Debt free Ramsey thinks. On a spreadsheet, it can look imperfect. If one balance has a high interest rate, and another balance is smaller but lower interest, a pure math approach may attack the higher rate first. That can reduce interest. That can be valid. But Ramsey style thinking asks a different question: what helps a real human stay in motion long enough to finish?\n\nThat question matters because debt payoff is not just arithmetic. It is a long emotional process. You are saying no to things. You are facing old decisions. You are changing habits while life is still happening. If the plan gives you no visible win for months, many people lose steam. They feel like they are sacrificing but nothing is changing. The snowball tries to solve that by creating early proof.\n\nPicture someone with five balances. One is $250. One is $800. One is $2,400. One is $6,000. One is much larger. The snowball says: line them up from smallest to largest, focus hard, and knock out the smallest first. The first win may not be the most mathematically efficient. But it changes the feeling of the plan. Now there are four payments instead of five. Now the person has a closed account. Now the brain gets evidence: I can finish things. That belief can matter.\n\nThis is why the snowball is not random. It is motivation design. It is built around the idea that progress has to be felt. Debt free Ramsey believes people often need emotional traction before they can handle a longer journey. The closed balance becomes a signal. You are not just moving money around. You are changing identity. You are becoming the kind of person who finishes obligations instead of dragging them forever.\n\nNow, the blind spot has to be named. The snowball can cost more in interest than attacking the highest rate first. For some people, that difference matters. For someone with the discipline to follow a rate based plan, the avalanche approach may feel more logical. Debt free Ramsey may underweight that. This is why Nisk should frame the snowball as a lens, not a command. It is a way of seeing the problem.\n\nThe useful question is not, which method is morally correct? The better question is, which plan would you actually finish? If a perfect plan sits ignored, it is not perfect in real life. If a simple plan creates momentum, that momentum has value. In Nisk, the user can compare scenarios and think about tradeoffs. How much interest might one path cost? How much motivation might another path create? What matters is learning how the worldview sees reality. Debt free Ramsey sees debt payoff as a fight for momentum, not just a contest of interest rates.',
			flashcards: [
				{
					id: 'school-debt-free-ramsey-02-fc-1',
					role: 'main_idea',
					front: 'Why small balances first?',
					back: 'Quick wins can build belief and keep someone moving.',
				},
				{
					id: 'school-debt-free-ramsey-02-fc-2',
					role: 'watch_out',
					front: 'What is the tradeoff?',
					back: 'The snowball may cost more interest than a rate based plan.',
				},
				{
					id: 'school-debt-free-ramsey-02-fc-3',
					role: 'use_it',
					front: 'What question matters?',
					back: 'Ask which payoff path you would actually finish.',
				},
			],
			applyAction: {
				type: 'openSimulator',
				label: 'Simulate a focused payoff plan',
			},
			revision: 1,
		},
		{
			id: 'school-debt-free-ramsey-03',
			title: 'Payments are stolen options',
			description:
				'This lesson teaches why Debt free Ramsey sees monthly payments as future choices already spent.',
			pillar: 'debt-credit-interest',
			schoolsOfThought: ['debt-free-ramsey'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['debt-free-ramsey', 'payment-drag', 'future-options'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'payment drag',
				'future options',
				'income already claimed',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'A monthly payment can look small by itself. Debt free Ramsey notices what happens when many small payments stack up and quietly claim your future.',
				},
				{
					type: 'paragraph',
					text: 'This lens treats payments as stolen options because each required payment reduces what your next paycheck can do for your actual life.',
				},
				{
					type: 'bullet_list',
					items: [
						'less room to save',
						'less room to move',
						'less room to recover',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is asking whether you can afford the payment instead of asking what the payment takes away.',
				},
				{
					type: 'example',
					text: 'A $420 car payment, $90 phone payment, and $160 card minimum can turn a good income into a tight month. The Ramsey lens says the danger is not only the total. It is the loss of choice.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is that not every payment is reckless, and some obligations may support stability, transportation, or work. The lens gets weaker when it treats all complexity as failure.',
				},
				{
					type: 'paragraph',
					text: 'In Nisk, look at your Dashboard and ask how much of your next month is already assigned before you make one new decision.',
				},
				{
					type: 'key_takeaway',
					text: 'Debt free Ramsey sees every payment as a claim on tomorrow.',
				},
			],
			audioScript:
				'One of the most useful things the Debt free Ramsey lens notices is how normal payments can become. A person may not feel like they are in trouble because each payment makes sense by itself. The car payment fits. The phone payment fits. The card minimum fits. The furniture payment fits. Maybe there is a personal loan too. Nothing looks shocking alone. But together, those payments start eating the future.\n\nThis is why Ramsey style thinking focuses so much on getting rid of payments. It is not only about being against debt as an idea. It is about what payments do to your options. Every required payment is money that cannot become runway. It cannot become a move out fund. It cannot become time off after burnout. It cannot become savings for a repair, a trip, a career switch, or a calmer month. That money already has a boss.\n\nA payment is basically a decision from the past showing up in the present. Sometimes that decision was understandable. Sometimes it was necessary. Sometimes it was a mistake. But once the payment exists, it limits what your next paycheck can become. Debt free Ramsey wants the user to feel that clearly. The question is not only, can I afford this? The sharper question is, what does this make harder later?\n\nTake a normal example. Someone earns enough to look stable from the outside. But before the month begins, they owe rent, a car payment, card minimums, a phone payment, insurance, and maybe a personal loan. After groceries and gas, there is almost no breathing room. From the outside, the income looks fine. From the inside, the person feels trapped. The Ramsey lens would say the problem is payment drag. The paycheck is not fully yours because the past keeps collecting from it.\n\nThis is why this worldview can be powerful for people who feel tired of starting every month behind. It gives a simple target: remove claims on your future income. When payments disappear, income starts feeling different. The same paycheck can create more choices. You may be able to save faster, handle emergencies with less panic, or make decisions without borrowing again.\n\nBut the blind spot matters. Not every payment means someone was careless. Some people need transportation for work. Some people are dealing with medical costs, caregiving, divorce, layoffs, or expensive housing. A rigid version of this lens can sound like it ignores reality. Nisk should avoid that. The point is not to judge the person. The point is to make the tradeoff visible.\n\nSo the next time a payment looks small, pause and translate it into options. What else could that monthly amount do? How much runway could it build over a year? How much less stress would your month have if it disappeared? That is the Debt free Ramsey lesson. Payments are not just bills. They are future choices already spent.',
			flashcards: [
				{
					id: 'school-debt-free-ramsey-03-fc-1',
					role: 'main_idea',
					front: 'What is payment drag?',
					back: 'Required payments that shrink what future paychecks can do.',
				},
				{
					id: 'school-debt-free-ramsey-03-fc-2',
					role: 'watch_out',
					front: 'What question is too weak?',
					back: 'Can I afford the payment?',
				},
				{
					id: 'school-debt-free-ramsey-03-fc-3',
					role: 'use_it',
					front: 'What question is stronger?',
					back: 'What option does this payment take away from future me?',
				},
			],
			applyAction: {
				type: 'openDashboard',
				label: 'Check your claimed income',
			},
			revision: 1,
		},
		{
			id: 'school-debt-free-ramsey-04',
			title: 'Simplicity beats constant debate',
			description:
				'This lesson teaches why Debt free Ramsey accepts less optimization in exchange for clarity and follow through.',
			pillar: 'debt-credit-interest',
			schoolsOfThought: ['debt-free-ramsey'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['debt-free-ramsey', 'simple-rules', 'decision-fatigue'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'simple rules',
				'clarity over optimization',
				'decision fatigue',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Some people stay stuck because they keep searching for the perfect financial move. Debt free Ramsey notices that endless comparison can become a hiding place.',
				},
				{
					type: 'paragraph',
					text: 'This lens values simple rules because simple rules reduce decision fatigue. When the order is clear, the user can act instead of debating forever.',
				},
				{
					type: 'bullet_list',
					items: [
						'fewer choices',
						'clearer priorities',
						'faster action',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is using complexity to avoid the discomfort of changing behavior.',
				},
				{
					type: 'example',
					text: 'Someone spends weeks comparing payoff orders, balance transfer ideas, and reward points. Meanwhile, no extra money moves toward the debt. The Ramsey lens says the plan is becoming a delay tactic.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that simple rules can be too blunt. They may ignore context, credit score goals, interest differences, or short term liquidity needs.',
				},
				{
					type: 'paragraph',
					text: 'In Nisk, use this lens as a reflection: which money decision are you overthinking because the next action feels uncomfortable?',
				},
				{
					type: 'key_takeaway',
					text: 'Debt free Ramsey uses simplicity to turn stuck energy into movement.',
				},
			],
			audioScript:
				'Debt free Ramsey is not trying to win every spreadsheet argument. That is important to understand. This worldview is built for people who need movement. It assumes that many people do not fail because they lack information. They fail because they are overwhelmed, tired, embarrassed, distracted, or stuck in a loop of comparison. More information does not always create more action. Sometimes more information becomes another way to delay the hard part.\n\nThat is where simplicity comes in. Ramsey style rules are often blunt on purpose. Save a small starter cushion. Attack debt with intensity. Use the snowball. Avoid adding new debt. Give every dollar a job. These ideas are not designed to impress a finance professor. They are designed to be remembered when life gets messy. If the rule is simple, the user does not have to redecide the whole plan every week.\n\nThis matters because decision fatigue is real. Imagine someone who has credit card debt, a car payment, irregular expenses, and a small emergency fund. They start reading arguments online. One person says optimize interest. Another says protect credit score. Another says invest while paying debt. Another says transfer balances. Another says use rewards. Some of those arguments may be reasonable in different contexts. But for this person, the result is paralysis. They spend three weeks researching and no money actually moves. Debt free Ramsey would say: stop worshiping complexity. Pick the next clear action and build momentum.\n\nThe strength of this worldview is that it cuts through noise. It gives the user a sequence. That sequence can reduce anxiety because the user does not have to solve every money question at once. The next step becomes obvious. That can be especially useful when someone is ashamed or overloaded. A simple plan can feel like a handrail.\n\nBut the blind spot is also obvious. Simple rules are not always the most efficient. They can flatten important differences. Someone with unusual income, legal issues, tax complexity, family obligations, or credit needs may require more nuance. A strict version of this lens can make every exception look like an excuse. That is not fair. Real life sometimes needs judgment.\n\nThe best way to teach this in Nisk is to frame it as a tradeoff. Debt free Ramsey is trading precision for action. It is saying that a clear plan completed imperfectly may beat a brilliant plan that never leaves the notes app. That does not mean simplicity is always right. It means simplicity has value when complexity is keeping you frozen.\n\nSo ask yourself: where are you using research to avoid action? Are you comparing plans because the decision truly needs more thought, or because the next step feels uncomfortable? That is the core of this lesson. Simplicity is not the absence of intelligence. Sometimes simplicity is how a tired person finally starts.',
			flashcards: [
				{
					id: 'school-debt-free-ramsey-04-fc-1',
					role: 'main_idea',
					front: 'Why simple rules?',
					back: 'They reduce decision fatigue and help people act.',
				},
				{
					id: 'school-debt-free-ramsey-04-fc-2',
					role: 'watch_out',
					front: 'What can simplicity miss?',
					back: 'Context, liquidity, credit goals, and interest differences.',
				},
				{
					id: 'school-debt-free-ramsey-04-fc-3',
					role: 'use_it',
					front: 'When does this help?',
					back: 'When complexity is turning into delay.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Find your delay loop',
				prompt: 'Write one money decision you keep researching instead of acting on, then write the simplest next step you could test.',
			},
			revision: 1,
		},
		{
			id: 'school-debt-free-ramsey-05',
			title: 'Where debt freedom gets too rigid',
			description:
				'This lesson teaches the humility piece of Debt free Ramsey: the lens can protect people, but it can also miss nuance.',
			pillar: 'debt-credit-interest',
			schoolsOfThought: ['debt-free-ramsey'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['debt-free-ramsey', 'rigidity-risk', 'context-matters'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'rigidity risk',
				'optimization blind spot',
				'context matters',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Debt free Ramsey can be powerful because it is clear. That same clarity can become a problem when it turns every situation into the same answer.',
				},
				{
					type: 'paragraph',
					text: 'This lens believes debt freedom creates peace, but its blind spot is that not every financial problem is solved by maximum debt urgency.',
				},
				{
					type: 'bullet_list',
					items: [
						'credit can have a role',
						'liquidity can matter',
						'context changes tradeoffs',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is turning a useful lens into a universal rule for every household.',
				},
				{
					type: 'example',
					text: 'Someone has debt, unstable income, and almost no emergency cushion. A rigid payoff plan might feel disciplined, but too little cash could push them back into debt after one repair. The better lesson is to understand the tradeoff clearly.',
				},
				{
					type: 'paragraph',
					text: 'Debt free Ramsey may underweight credit optimization, flexible planning, and situations where preserving cash reduces risk. That does not make the lens useless. It means the user should know what it is built to notice and what it may miss.',
				},
				{
					type: 'paragraph',
					text: 'In Nisk, compare this worldview against your real numbers. Ask whether you need urgency, flexibility, or a mix of both.',
				},
				{
					type: 'key_takeaway',
					text: 'A strong lens helps you see clearly, but no lens sees everything.',
				},
			],
			audioScript:
				'The best way to understand any school of thought is to know both its power and its limit. Debt free Ramsey has power because it is clear. It gives people a simple enemy: debt. It gives people a simple direction: get rid of payments. It gives people a simple reason: peace, control, and future options. For someone drowning in bills, that clarity can feel like oxygen.\n\nBut the same clarity can become rigid. A worldview that helps one person move can make another person feel boxed in. Debt free Ramsey can sometimes talk as if all debt questions are settled before the context is even known. That is the blind spot. Real households are not all in the same position. Some people have unstable income. Some people are supporting family. Some people need reliable transportation. Some people have medical bills. Some people are rebuilding after a hard year. Some people are trying to protect cash because one emergency would send them right back into borrowing.\n\nThis does not mean the debt free lens is wrong. It means it is a lens. It is built to notice behavior, urgency, payment fatigue, and the emotional burden of owing money. It is less built to notice optimization, credit strategy, liquidity planning, or edge cases where flexibility matters more than maximum payoff speed. That distinction is important. Nisk should teach the worldview without turning it into a command.\n\nTake a simple example. Imagine someone has a few thousand dollars of debt and only a tiny cash cushion. A rigid version of the lens might say to throw every extra dollar at the balance. That may create fast progress. But if the person has a car repair next month and no cash, they may end up borrowing again. A more nuanced view asks: what is the risk of moving too fast? What is the risk of moving too slowly? What does this person need most right now: urgency, breathing room, or a mix?\n\nAnother example is credit. Debt free Ramsey often treats credit dependence as dangerous, and that warning can protect people who use credit to avoid reality. But some users may care about renting an apartment, qualifying for better rates, or understanding credit behavior. A lens that ignores that entirely can miss practical realities. The answer is not to worship credit. The answer is to understand what each lens is noticing.\n\nThis final lesson matters because schools should not become tribes. The point is not to pick a money identity and defend it forever. The point is to learn what the worldview sees clearly, then test it against your life. Debt free Ramsey sees the danger of debt becoming normal. That is useful. It sees the emotional power of payoff momentum. That is useful too. But it can get too blunt when it treats every situation as the same.\n\nSo use the lens with respect and with judgment. Ask what it reveals. Ask what it misses. If it helps you face debt honestly, good. If it makes you ignore real context, slow down and compare the tradeoffs. A strong lens helps you see clearly, but no lens sees everything.',
			flashcards: [
				{
					id: 'school-debt-free-ramsey-05-fc-1',
					role: 'main_idea',
					front: 'What is the blind spot?',
					back: 'The lens can underweight nuance, liquidity, credit strategy, and context.',
				},
				{
					id: 'school-debt-free-ramsey-05-fc-2',
					role: 'watch_out',
					front: 'What goes wrong?',
					back: 'A helpful rule can become too rigid for real life.',
				},
				{
					id: 'school-debt-free-ramsey-05-fc-3',
					role: 'use_it',
					front: 'How should you use it?',
					back: 'Ask what the lens reveals, then test it against your actual situation.',
				},
			],
			applyAction: {
				type: 'buildFreedomPlan',
				label: 'Build a plan with tradeoffs',
			},
			revision: 1,
		},
	],
};
